import { useAutoneTranslation } from '@autone/translations';
import {
  AdvancedFiltersClearAllButton,
  AdvancedFiltersContent,
  cn,
  FilterPopoverContent,
  FilterPopoverFooter,
  FilterPopoverHeader,
  FilterPopoverHeaderTitle,
  FiltersPopoverSwitchFilterViewButton,
  GeneralFiltersClearAllButton,
  GeneralFiltersContent,
  TailwindPopoverContent,
  type TailwindPopoverContentProps,
} from '@autone/ui';
import { memo, useState } from 'react';

import { type APP_NAME } from '../../../consts';
import { useGetAvailableFilterStates } from '../../../hooks/use-get-available-filter-states';
import { type TableNamesWithAnyFilters } from '../types';

import { FiltersPopoverContext } from './FiltersPopover.context';
import { type FilterView } from './FiltersPopoverContent.types';

export type FiltersPopoverContentProps = Omit<
  {
    tableName: TableNamesWithAnyFilters;
  } & TailwindPopoverContentProps,
  'children'
>;

const FiltersPopoverContentBase = ({
  tableName,
  className,
  ...props
}: FiltersPopoverContentProps) => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();
  const availableFilters = useGetAvailableFilterStates(tableName);
  const [filterView, setFilterView] = useState<FilterView>(availableFilters[0]);

  const canRenderBothFilters = availableFilters.length === 2;

  return (
    <TailwindPopoverContent
      className={cn(
        'p-0 w-[800px] h-[400px] shadow-xl overflow-hidden',
        className,
      )}
      {...props}
    >
      <FiltersPopoverContext filterView={filterView} tableName={tableName}>
        <FilterPopoverContent className="border-none">
          <FilterPopoverHeader>
            <div className="flex flex-row items-center gap-2">
              <FilterPopoverHeaderTitle>
                {filterView === 'general' &&
                  common('filters.general-filters-header')}
                {filterView === 'advanced' &&
                  common('filters.advanced-filters-header')}
              </FilterPopoverHeaderTitle>
              {canRenderBothFilters && (
                <FiltersPopoverSwitchFilterViewButton
                  filterView={filterView}
                  onFilterViewChange={setFilterView}
                />
              )}
            </div>
          </FilterPopoverHeader>
          {filterView === 'general' && <GeneralFiltersContent />}
          {filterView === 'advanced' && <AdvancedFiltersContent.V2 />}
          <FilterPopoverFooter>
            {filterView === 'general' && (
              <GeneralFiltersClearAllButton>
                {common('filters.clear-all-selections')}
              </GeneralFiltersClearAllButton>
            )}
            {filterView === 'advanced' && (
              <AdvancedFiltersClearAllButton.V2>
                {common('filters.clear-all-selections')}
              </AdvancedFiltersClearAllButton.V2>
            )}
          </FilterPopoverFooter>
        </FilterPopoverContent>
      </FiltersPopoverContext>
    </TailwindPopoverContent>
  );
};

// safe to memoize since data bindings are purely dependent on props
export const FiltersPopoverContent = memo(FiltersPopoverContentBase);
